import { clsx } from "clsx";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import {
	type DayButton,
	DayPicker,
	getDefaultClassNames,
} from "react-day-picker";

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	captionLayout = "label",
	formatters,
	components,
	...props
}: React.ComponentProps<typeof DayPicker>) {
	const defaultClassNames = getDefaultClassNames();

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={clsx("calendar", className)}
			captionLayout={captionLayout}
			formatters={{
				formatMonthDropdown: (date) =>
					date.toLocaleString("default", { month: "short" }),
				...formatters,
			}}
			classNames={{
				root: clsx("calendar__root", defaultClassNames.root),
				months: clsx("calendar__months", defaultClassNames.months),
				month: clsx("calendar__month", defaultClassNames.month),
				nav: clsx("calendar__nav", defaultClassNames.nav),
				button_previous: clsx(
					"calendar__nav-button",
					defaultClassNames.button_previous,
				),
				button_next: clsx(
					"calendar__nav-button",
					defaultClassNames.button_next,
				),
				month_caption: clsx(
					"calendar__caption",
					defaultClassNames.month_caption,
				),
				dropdowns: clsx("calendar__dropdowns", defaultClassNames.dropdowns),
				dropdown_root: clsx(
					"calendar__dropdown-root",
					defaultClassNames.dropdown_root,
				),
				dropdown: clsx("calendar__dropdown", defaultClassNames.dropdown),
				caption_label: clsx(
					"calendar__caption-label",
					defaultClassNames.caption_label,
				),
				table: "calendar__table",
				weekdays: clsx("calendar__weekdays", defaultClassNames.weekdays),
				weekday: clsx("calendar__weekday", defaultClassNames.weekday),
				week: clsx("calendar__week", defaultClassNames.week),
				week_number_header: clsx(
					"calendar__week-number-header",
					defaultClassNames.week_number_header,
				),
				week_number: clsx(
					"calendar__week-number",
					defaultClassNames.week_number,
				),
				day: clsx("calendar__day", "group/day", defaultClassNames.day),
				range_start: clsx(
					"calendar__range-start",
					defaultClassNames.range_start,
				),
				range_middle: clsx(
					"calendar__range-middle",
					defaultClassNames.range_middle,
				),
				range_end: clsx("calendar__range-end", defaultClassNames.range_end),
				today: clsx("calendar__today", defaultClassNames.today),
				outside: clsx("calendar__outside", defaultClassNames.outside),
				disabled: clsx("calendar__disabled", defaultClassNames.disabled),
				hidden: clsx("calendar__hidden", defaultClassNames.hidden),
				...classNames,
			}}
			components={{
				Root: ({ className, rootRef, ...props }) => {
					return (
						<div
							data-slot="calendar"
							ref={rootRef}
							className={clsx(className)}
							{...props}
						/>
					);
				},
				Chevron: ({ className, orientation, ...props }) => {
					if (orientation === "left") {
						return (
							<ChevronLeft
								className={clsx("calendar__chevron", className)}
								{...props}
							/>
						);
					}

					if (orientation === "right") {
						return (
							<ChevronRight
								className={clsx("calendar__chevron", className)}
								{...props}
							/>
						);
					}

					return (
						<ChevronDown
							className={clsx("calendar__chevron", className)}
							{...props}
						/>
					);
				},
				DayButton: CalendarDayButton,
				WeekNumber: ({ children, ...props }) => {
					return (
						<td {...props}>
							<div className="calendar__week-number-cell">{children}</div>
						</td>
					);
				},
				...components,
			}}
			{...props}
		/>
	);
}

function CalendarDayButton({
	className,
	day,
	modifiers,
	...props
}: React.ComponentProps<typeof DayButton>) {
	const defaultClassNames = getDefaultClassNames();

	const ref = React.useRef<HTMLButtonElement>(null);
	React.useEffect(() => {
		if (modifiers.focused) ref.current?.focus();
	}, [modifiers.focused]);

	return (
		<button
			ref={ref}
			type="button"
			data-day={day.date.toLocaleDateString()}
			data-selected-single={
				modifiers.selected &&
				!modifiers.range_start &&
				!modifiers.range_end &&
				!modifiers.range_middle
			}
			data-range-start={modifiers.range_start}
			data-range-end={modifiers.range_end}
			data-range-middle={modifiers.range_middle}
			className={clsx("calendar__day-button", defaultClassNames.day, className)}
			{...props}
		/>
	);
}

export { Calendar, CalendarDayButton };
